import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Checkbox,
  Dropdown,
  Option,
  Divider,
  tokens,
} from '@fluentui/react-components';
import { PeopleAddRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn, type FilingType, type BusinessEntityType, filingTypeDisplayNames, amendmentToggleLabel } from '../../../types';

export interface EditClientNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  currentAliases: string[];
  currentTaxYear: number;
  currentFilingType: FilingType;
  returnType: 'individual' | 'business';
  businessEntityType?: BusinessEntityType;
  hasSpouse: boolean;
  currentIsAmendment?: boolean;
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
  onAddSpouse?: () => void;
}

// Individual filing status options (excluding business)
const individualFilingTypes: FilingType[] = ['single', 'mfs', 'hoh', 'joint'];

// Generate tax year options (current year and previous 5 years)
const currentYear = new Date().getFullYear();
const taxYearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

export function EditClientNameDialog({
  open,
  onOpenChange,
  currentName,
  currentAliases,
  currentTaxYear,
  currentFilingType,
  returnType,
  businessEntityType,
  hasSpouse,
  currentIsAmendment = false,
  updateMutation,
  onAddSpouse,
}: EditClientNameDialogProps) {
  const [newName, setNewName] = useState(currentName);
  const [keepOldName, setKeepOldName] = useState(true);
  const [taxYear, setTaxYear] = useState(currentTaxYear);
  const [filingType, setFilingType] = useState<FilingType>(currentFilingType);
  const [isAmendment, setIsAmendment] = useState(currentIsAmendment);
  const [showAddSpousePrompt, setShowAddSpousePrompt] = useState(false);

  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setKeepOldName(true);
      setTaxYear(currentTaxYear);
      setFilingType(currentFilingType);
      setIsAmendment(currentIsAmendment);
      setShowAddSpousePrompt(false);
    }
  }, [open, currentName, currentTaxYear, currentFilingType, currentIsAmendment]);

  // Check if changing to MFJ from another status and no spouse exists
  const isChangingToMfj = filingType === 'joint' && currentFilingType !== 'joint' && !hasSpouse;

  const handleSave = () => {
    const updates: Partial<TaxReturn> = {};
    let hasChanges = false;

    // Handle name change
    if (newName.trim() && newName.trim() !== currentName) {
      updates.clientName = newName.trim();
      hasChanges = true;

      if (keepOldName && currentName.trim()) {
        const updatedAliases = [...(currentAliases || [])];
        if (!updatedAliases.includes(currentName.trim())) {
          updatedAliases.push(currentName.trim());
        }
        updates.searchableAliases = updatedAliases;
      }
    }

    // Handle tax year change
    if (taxYear !== currentTaxYear) {
      updates.taxYear = taxYear;
      hasChanges = true;
    }

    // Handle filing type change (only for individuals)
    if (returnType === 'individual' && filingType !== currentFilingType) {
      updates.filingType = filingType;
      hasChanges = true;

      // If changing away from joint, clear spouse info
      if (currentFilingType === 'joint' && filingType !== 'joint') {
        updates.spouseId = null;
        updates.spouseName = null;
        updates.spouseEmail = null;
        updates.spousePhone = null;
      }
    }

    // Handle amendment flag change (any return type — individuals file 1040X, businesses amend
    // on their base form).
    if (isAmendment !== currentIsAmendment) {
      updates.isAmendment = isAmendment;
      hasChanges = true;
    }

    if (!hasChanges) {
      onOpenChange(false);
      return;
    }

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
        // If changed to MFJ and wants to add spouse, trigger the add spouse dialog
        if (isChangingToMfj && showAddSpousePrompt && onAddSpouse) {
          setTimeout(() => onAddSpouse(), 100);
        }
      },
    });
  };

  const hasChanges =
    (newName.trim() && newName.trim() !== currentName) ||
    taxYear !== currentTaxYear ||
    (returnType === 'individual' && filingType !== currentFilingType) ||
    (isAmendment !== currentIsAmendment);

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface style={{ maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>Edit Return Information</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Client Name Section */}
              <Field label="Current Name">
                <Input value={currentName} disabled />
              </Field>
              <Field label="New Name">
                <Input
                  value={newName}
                  onChange={(_, data) => setNewName(data.value)}
                  placeholder="Enter new client name"
                />
              </Field>
              {newName.trim() && newName.trim() !== currentName && (
                <>
                  <Checkbox
                    checked={keepOldName}
                    onChange={(_, data) => setKeepOldName(data.checked === true)}
                    label="Keep old name searchable"
                  />
                  {keepOldName && (
                    <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)', marginTop: '-8px' }}>
                      "{currentName}" will be saved as a searchable alias so you can still find this return by the old name.
                    </div>
                  )}
                </>
              )}

              <Divider style={{ margin: '8px 0' }} />

              {/* Tax Year Section */}
              <Field label="Tax Year">
                <Dropdown
                  value={String(taxYear)}
                  selectedOptions={[String(taxYear)]}
                  onOptionSelect={(_, data) => setTaxYear(Number(data.optionValue))}
                >
                  {taxYearOptions.map((year) => (
                    <Option key={year} value={String(year)} text={String(year)}>
                      {year}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              {/* Amendment flag — any return type. Individuals file 1040X; businesses amend on
                  their base form, so the label is type-aware. */}
              <Checkbox
                checked={isAmendment}
                onChange={(_, data) => setIsAmendment(data.checked === true)}
                label={amendmentToggleLabel(returnType, businessEntityType)}
              />

              {/* Filing Status Section (only for individuals) */}
              {returnType === 'individual' && (
                <>
                  <Field label="Filing Status">
                    <Dropdown
                      value={filingTypeDisplayNames[filingType]}
                      selectedOptions={[filingType]}
                      onOptionSelect={(_, data) => {
                        const newFilingType = data.optionValue as FilingType;
                        setFilingType(newFilingType);
                        // Reset add spouse prompt when changing filing type
                        setShowAddSpousePrompt(newFilingType === 'joint' && currentFilingType !== 'joint' && !hasSpouse);
                      }}
                    >
                      {individualFilingTypes.map((type) => (
                        <Option key={type} value={type}>
                          {filingTypeDisplayNames[type]}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>

                  {/* MFJ Add Spouse Prompt */}
                  {isChangingToMfj && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f0f5ff',
                      border: '1px solid #c7d9ff',
                      borderRadius: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                        <PeopleAddRegular style={{ fontSize: '18px', color: '#0078d4' }} />
                        Add Spouse Information?
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--tl-text-primary)' }}>
                        Married Filing Jointly requires spouse information. Would you like to add the spouse after saving?
                      </div>
                      <Checkbox
                        checked={showAddSpousePrompt}
                        onChange={(_, data) => setShowAddSpousePrompt(data.checked === true)}
                        label="Yes, open Add Spouse dialog after saving"
                      />
                    </div>
                  )}

                  {/* Warning when changing away from joint with spouse */}
                  {currentFilingType === 'joint' && filingType !== 'joint' && hasSpouse && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#fff4ce',
                      border: '1px solid #ffb900',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: 'var(--tl-text-primary)',
                    }}>
                      <strong>Note:</strong> Changing from Married Filing Jointly will remove the spouse information from this return.
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!hasChanges || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
